import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StoreIcon from '@mui/icons-material/Store';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    user_type: 'client',
    motoboy_scope: 'LOJA',
    lanchonete_id: '',
    active: true
  });
  const { isAdminMaster, isAdminLanchonete } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usuariosResponse, lanchonetesResponse] = await Promise.all([
        api.get('/users'),
        api.get('/lanchonetes')
      ]);
      setUsuarios(usuariosResponse.data);
      setLanchonetes(lanchonetesResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleOpenDialog = (usuario = null) => {
    setShowPassword(false);
    if (usuario) {
      setEditingUsuario(usuario);
      setFormData({
        name: usuario.name,
        email: usuario.email,
        password: '',
        phone: usuario.phone,
        address: usuario.address,
        user_type: usuario.user_type,
        motoboy_scope: usuario.motoboy_scope || 'LOJA',
        lanchonete_id: usuario.lanchonete_id || '',
        active: usuario.active
      });
    } else {
      setEditingUsuario(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        user_type: isAdminMaster() ? 'client' : 'waiter',
        motoboy_scope: 'LOJA',
        lanchonete_id: '',
        active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUsuario(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = { ...formData };
      
      // Só incluir senha se foi preenchida
      if (!submitData.password) {
        delete submitData.password;
      }
      
      if (editingUsuario) {
        if (!submitData.lanchonete_id) {
          submitData.lanchonete_id = null;
        }

        await api.put(`/users/${editingUsuario.id}`, submitData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        if (!submitData.password) {
          toast.error('Senha é obrigatória para novos usuários');
          return;
        }
        
        // Usar rotas específicas para cada tipo de usuário
        if (['cashier', 'kitchen', 'waiter'].includes(submitData.user_type)) {
          await api.post('/users/staff', {
            name: submitData.name,
            email: submitData.email,
            password: submitData.password,
            phone: submitData.phone,
            role: submitData.user_type,
            active: submitData.active
          });
          toast.success('Funcionario criado com sucesso!');
        } else if (submitData.user_type === 'admin_lanchonete') {
          if (!submitData.lanchonete_id) {
            toast.error('ID da lanchonete é obrigatório para admin lanchonete');
            return;
          }
          await api.post('/users/admin', submitData);
          toast.success('Admin lanchonete criado com sucesso!');
        } else if (submitData.user_type === 'motoboy') {
          if (!submitData.phone) {
            toast.error('Telefone é obrigatório para motoboy');
            return;
          }
          if (submitData.motoboy_scope === 'LOJA' && !submitData.lanchonete_id) {
            toast.error('ID da lanchonete é obrigatório para motoboy de loja');
            return;
          }
          if (submitData.motoboy_scope === 'APP') {
            submitData.lanchonete_id = null;
          }
          await api.post('/users/motoboy', submitData);
          toast.success('Motoboy criado com sucesso!');
        } else {
          await api.post('/users', submitData);
          toast.success('Usuário criado com sucesso!');
        }
      }
      
      handleCloseDialog();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await api.delete(`/users/${id}`);
        toast.success('Usuário excluído com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir usuário');
      }
    }
  };

  const handleToggleStatus = async (id, active) => {
    try {
      // Enviar para API primeiro
      await api.put(`/users/${id}`, { active });
      toast.success(`Usuário ${active ? 'ativado' : 'desativado'} com sucesso!`);
      
      // Recarregar dados do servidor para garantir consistência
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status');
      // Em caso de erro, recarregar dados para mostrar estado real
      loadData();
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case 'admin_master': return 'error';
      case 'admin_lanchonete': return 'warning';
      case 'motoboy': return 'info';
      case 'cashier': return 'success';
      case 'kitchen': return 'secondary';
      case 'waiter': return 'primary';
      default: return 'default';
    }
  };

  const getUserTypeText = (type) => {
    switch (type) {
      case 'admin_master': return 'Admin Master';
      case 'admin_lanchonete': return 'Admin Lanchonete';
      case 'motoboy': return 'Motoboy';
      case 'client': return 'Cliente';
      case 'cashier': return 'Caixa';
      case 'kitchen': return 'Cozinha';
      case 'waiter': return 'Garcom';
      default: return type;
    }
  };

  const getLanchoneteName = (lanchoneteId) => {
    const lanchonete = lanchonetes.find(l => l.id === lanchoneteId);
    return lanchonete ? lanchonete.name : 'Não atribuído';
  };

  if (!isAdminMaster() && !isAdminLanchonete()) {
    return (
      <Alert severity="warning">
        Acesso restrito. Apenas o administrador master pode gerenciar usuários.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Usuários
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Usuário
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Lanchonete</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((usuario) => (
              <TableRow key={usuario.id}>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {usuario.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">{usuario.email}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    {usuario.phone && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{usuario.phone}</Typography>
                      </Box>
                    )}
                    {usuario.address && (
                      <Typography variant="caption" color="textSecondary">
                        {usuario.address}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getUserTypeText(usuario.user_type)}
                    color={getUserTypeColor(usuario.user_type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {usuario.lanchonete_id ? (
                    <Box display="flex" alignItems="center">
                      <StoreIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {getLanchoneteName(usuario.lanchonete_id)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(usuario.active)}
                        onChange={(e) => handleToggleStatus(usuario.id, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={Boolean(usuario.active) ? 'Ativo' : 'Inativo'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(usuario)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(usuario.id)}
                    disabled={usuario.user_type === 'admin_master'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="name"
                label="Nome"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                name="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                helperText={editingUsuario ? "Deixe em branco para manter a senha atual" : ""}
                required={!editingUsuario}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      onClick={() => setShowPassword((visible) => !visible)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
              <TextField
                name="phone"
                label="Telefone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="address"
                label="Endereço"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
              <FormControl fullWidth>
                <InputLabel>Tipo de Usuário</InputLabel>
                <Select
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleInputChange}
                  label="Tipo de Usuário"
                >
                  {isAdminMaster() && <MenuItem value="client">Cliente</MenuItem>}
                  {isAdminMaster() && <MenuItem value="admin_lanchonete">Admin Lanchonete</MenuItem>}
                  {isAdminMaster() && <MenuItem value="motoboy">Motoboy</MenuItem>}
                  <MenuItem value="cashier">Caixa</MenuItem>
                  <MenuItem value="kitchen">Cozinha</MenuItem>
                  <MenuItem value="waiter">Garcom</MenuItem>
                </Select>
              </FormControl>
              
              {formData.user_type === 'motoboy' && (
                <FormControl fullWidth>
                  <InputLabel>Escopo do Motoboy</InputLabel>
                  <Select
                    name="motoboy_scope"
                    value={formData.motoboy_scope}
                    onChange={handleInputChange}
                    label="Escopo do Motoboy"
                  >
                    <MenuItem value="LOJA">LOJA</MenuItem>
                    <MenuItem value="APP">APP</MenuItem>
                  </Select>
                </FormControl>
              )}

              {['admin_lanchonete', 'motoboy'].includes(formData.user_type) &&
                !(formData.user_type === 'motoboy' && formData.motoboy_scope === 'APP') && (
                <FormControl fullWidth>
                  <InputLabel>Lanchonete</InputLabel>
                  <Select
                    name="lanchonete_id"
                    value={formData.lanchonete_id}
                    onChange={handleInputChange}
                    label="Lanchonete"
                    required={formData.user_type === 'admin_lanchonete' || (formData.user_type === 'motoboy' && formData.motoboy_scope === 'LOJA')}
                  >
                    {lanchonetes.map(lanchonete => (
                      <MenuItem key={lanchonete.id} value={lanchonete.id}>
                        {lanchonete.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Usuário Ativo"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingUsuario ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Usuarios;
